import { useEffect } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
	Input,
	InputError,
	InputLabel,
	InputWrapper,
} from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { CircleChevronLeft, FileText, Image, Save, Type } from 'lucide-react';

const blogPostSchema = z.object({
	title: z
		.string()
		.min(1, 'Título é obrigatório')
		.max(500, 'Título deve ter no máximo 500 caracteres'),
	banner_url: z
		.string()
		.min(1, 'URL do banner é obrigatória')
		.url('Informe uma URL válida (https://...)'),
	created_at: z.string().optional(),
	content: z
		.string()
		.min(1, 'Conteúdo é obrigatório')
		.max(100000, 'Conteúdo deve ter no máximo 100.000 caracteres'),
});

type BlogPostFormData = z.infer<typeof blogPostSchema>;

interface BlogPostFormProps {
	onSubmit: (data: BlogPostFormData) => void;
	onBack: () => void;
	defaultValues?: Partial<Blog.BlogPost>;
	isSubmitting?: boolean;
	formTitle?: string;
	submitButtonText?: string;
	onValuesChange?: (values: BlogPostFormData) => void;
}

const BlogPostForm: React.FC<BlogPostFormProps> = ({
	onSubmit,
	onBack,
	defaultValues,
	isSubmitting: externalIsSubmitting = false,
	formTitle,
	submitButtonText,
	onValuesChange,
}) => {
	const isEditMode = !!defaultValues?.id;

	const {
		register,
		handleSubmit,
		control,
		reset,
		formState: { errors, isSubmitting: internalIsSubmitting, isDirty },
	} = useForm<BlogPostFormData>({
		resolver: zodResolver(blogPostSchema),
		defaultValues: {
			title: defaultValues?.title ?? '',
			banner_url: defaultValues?.banner_url ?? '',
			created_at: defaultValues?.created_at ?? '',
			content: defaultValues?.content ?? '',
		},
	});

	useEffect(() => {
		if (!defaultValues?.id) return;
		reset({
			title: defaultValues.title ?? '',
			banner_url: defaultValues.banner_url ?? '',
			created_at: defaultValues.created_at ?? '',
			content: defaultValues.content ?? '',
		});
	}, [defaultValues?.id, reset]);

	const isSubmitting = externalIsSubmitting || internalIsSubmitting;

	const watchedValues = useWatch({ control });
	useEffect(() => {
		if (onValuesChange) {
			onValuesChange(watchedValues as BlogPostFormData);
		}
	}, [watchedValues, onValuesChange]);

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className="max-w-2xl space-y-6 bg-inherit"
		>
			<h2 className="text-lg font-bold text-dark">{formTitle}</h2>

			<div className="space-y-1">
				<InputLabel
					text="Título"
					icon={<Type className="h-4 w-4 text-dark" />}
				/>
				<InputWrapper>
					<Input
						placeholder="Ex: Como cuidar da saúde mental no trabalho"
						{...register('title')}
					/>
				</InputWrapper>
				{errors.title?.message && (
					<InputError error={errors.title.message} />
				)}
			</div>

			<div className="space-y-1">
				<InputLabel
					text="URL do Banner"
					icon={<Image className="h-4 w-4 text-dark" />}
				/>
				<InputWrapper>
					<Input
						placeholder="https://exemplo.com/imagem.jpg"
						{...register('banner_url')}
					/>
				</InputWrapper>
				{errors.banner_url?.message && (
					<InputError error={errors.banner_url.message} />
				)}
			</div>

			<div className="space-y-1">
				<InputLabel text="Data de publicação (opcional)" />
				<Controller
					name="created_at"
					control={control}
					render={({ field }) => (
						<DatePicker
							placeholder="DD/MM/AAAA"
							clearable
							defaultValue={
								field.value ? new Date(field.value) : null
							}
							onChange={(date) =>
								field.onChange(date ? date.toISOString() : '')
							}
						/>
					)}
				/>
				{errors.created_at?.message && (
					<InputError error={errors.created_at.message} />
				)}
			</div>

			<div className="space-y-1">
				<InputLabel
					text="Conteúdo (Markdown)"
					icon={<FileText className="h-4 w-4 text-dark" />}
				/>
				<textarea
					className="min-h-[300px] w-full resize-y rounded-md border border-zinc-200 bg-inherit px-3 py-2 text-sm text-dark placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-300"
					placeholder="Escreva o conteúdo em Markdown..."
					{...register('content')}
				/>
				{errors.content?.message && (
					<InputError error={errors.content.message} />
				)}
			</div>

			<div className="flex justify-between">
				<Button
					onClick={onBack}
					text="Voltar"
					type="button"
					variant="outline"
					startIcon={<CircleChevronLeft className="w-4 text-dark" />}
				/>
				<Button
					type="submit"
					disabled={isSubmitting || (isEditMode && !isDirty)}
					endIcon={<Save className="w-4 text-light" />}
					text={submitButtonText ?? 'Salvar'}
				/>
			</div>
		</form>
	);
};

export { BlogPostForm };
